import axios from 'axios';
import { authHeader } from '@/lib/events';

/**
 * The rep sales workspace client (dark: the API answers 403 to anyone not on
 * its allow-list, superadmins included, and the UI then shows nothing).
 */

export const REP_CONNECTION_STATUS = {
  notConnected: 'not_connected',
  connected: 'connected',
  needsReconnect: 'needs_reconnect',
} as const;
export type RepConnectionStatus = (typeof REP_CONNECTION_STATUS)[keyof typeof REP_CONNECTION_STATUS];

export interface RepConnection {
  readonly status: RepConnectionStatus;
  readonly salesforceUsername: string | null;
  readonly connectedAt: string | null;
  /** False when the server lacks the connected app or token key. */
  readonly configured: boolean;
}

/** The query param the OAuth callback lands with, and its values. */
export const CONNECT_RESULT_PARAM = 'salesforce';
export const CONNECT_RESULT = { connected: 'connected', error: 'error' } as const;

const BASE = '/api/v3/sales-workspace/salesforce';

/** True when the caller is simply not on the allow-list. */
export function isNotAllowed(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

export async function readRepConnection(signal?: AbortSignal): Promise<RepConnection> {
  const response = await axios.get<{ data: RepConnection }>(`${BASE}/connection`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** The Salesforce sign-in URL; Salesforce sends the browser back to this profile. */
export async function createRepConnectUrl(): Promise<string> {
  const response = await axios.post<{ data: { url: string } }>(
    `${BASE}/connect-url`,
    { returnTo: window.location.origin },
    { headers: await authHeader() },
  );
  return response.data.data.url;
}

export async function disconnectRepConnection(): Promise<void> {
  await axios.delete(`${BASE}/connection`, { headers: await authHeader() });
}

/** The connection query's key; the profile section and the workspace pages share it. */
export const REP_CONNECTION_QUERY_KEY = ['rep-salesforce-connection'] as const;

/** Whether this connection can write to Salesforce as the rep right now. */
export function canWriteAsRep(connection: RepConnection | undefined): boolean {
  return connection?.configured === true && connection.status === REP_CONNECTION_STATUS.connected;
}

// ---- My day + opportunity page ---------------------------------------------

export const SALES_WORKSPACE_PATH = '/sales-workspace';
export const salesOpportunityPath = (id: string): string => `${SALES_WORKSPACE_PATH}/opportunities/${encodeURIComponent(id)}`;

/** Router state a deal link carries: the view it was opened from. */
export interface SalesReturnState {
  readonly from: string;
}

/** Where a deal page's back arrow returns: the view it came from, when that was a sales workspace view. */
export function salesReturnTo(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('from' in state)) return null;
  const { from } = state;
  return typeof from === 'string' && from.startsWith(SALES_WORKSPACE_PATH) ? from : null;
}

export const FOLLOW_UP_BUCKET = { overdue: 'overdue', today: 'today', upcoming: 'upcoming' } as const;
export type FollowUpBucket = (typeof FOLLOW_UP_BUCKET)[keyof typeof FOLLOW_UP_BUCKET];

export interface DealSummary {
  readonly id: string;
  readonly name: string;
  readonly accountName: string;
  readonly stageName: string;
}

export interface FollowUp {
  readonly taskId: string;
  readonly subject: string;
  readonly description: string;
  /** `yyyy-mm-dd`, or null when undated. */
  readonly dueDate: string | null;
  readonly bucket: FollowUpBucket;
  readonly lastModified: string;
  readonly ownerId: string;
  readonly opportunity: DealSummary;
}

export interface Deal extends DealSummary {
  readonly eventDate: string | null;
  readonly participants: number | null;
  readonly programName: string;
  readonly totalCost: number | null;
  readonly lastModified: string;
  readonly nextFollowUp: string | null;
}


export interface DealDetail extends Deal {
  readonly ownerId: string;
  readonly ownerName: string;
  readonly isClosed: boolean;
  readonly isWon: boolean;
  readonly reasonLost: string;
  readonly closeDate: string | null;
  readonly programId: string;
  readonly priceList: string;
  readonly perPersonPrice: number | null;
  readonly listPrice: number | null;
  readonly totalProgramCost: number | null;
  readonly eventCity: string;
  readonly eventState: string;
  readonly mirroredAt: string;
}

export const ACTIVITY_KIND = { email: 'email', call: 'call', todo: 'todo', stage: 'stage' } as const;
export type ActivityKind = (typeof ACTIVITY_KIND)[keyof typeof ACTIVITY_KIND];

export interface Activity {
  readonly id: string;
  readonly kind: ActivityKind;
  readonly title: string;
  readonly at: string;
}

export interface OpportunityPage {
  readonly today: string;
  readonly deal: DealDetail;
  readonly followUps: readonly FollowUp[];
  readonly activity: readonly Activity[];
}

/** The fields the edit forms change, under the API's names. */
export const OPPORTUNITY_FIELD = {
  name: 'name',
  stageName: 'stageName',
  reasonLost: 'reasonLost',
  programId: 'programId',
  eventDate: 'eventDate',
  participants: 'participants',
  priceList: 'priceList',
  perPersonPrice: 'perPersonPrice',
  listPrice: 'listPrice',
  totalProgramCost: 'totalProgramCost',
} as const;
export type OpportunityField = (typeof OPPORTUNITY_FIELD)[keyof typeof OPPORTUNITY_FIELD];

export interface OpportunityEditContext {
  /** Send back as `expectedLastModified`. */
  readonly lastModified: string;
  readonly signed: boolean;
  readonly locked: boolean;
  readonly values: Readonly<Record<OpportunityField, string | number | null>>;
  readonly programName: string | null;
  readonly fields: Readonly<Record<OpportunityField, { readonly editable: boolean; readonly options: readonly string[] }>>;
  readonly programs: ReadonlyArray<{ readonly id: string; readonly name: string }>;
}

export type OpportunityFieldChanges = Partial<Record<OpportunityField, string | number | null>>;

export interface RepWriteApplied {
  readonly status: 'applied';
  readonly mode: 'live' | 'dry-run';
  readonly affectedIds: readonly string[];
  readonly mirrorPatched: boolean;
}

/** Refusal codes the write routes answer 409 with (details.reason). */
export const REP_WRITE_REFUSAL = {
  stale: 'stale',
  notConnected: 'not_connected',
  reconnectRequired: 'reconnect_required',
} as const;

/** The refusal code of a failed write, if it was one. */
export function writeRefusal(error: unknown): string | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;
  const details = (error.response.data as { details?: { reason?: unknown } } | undefined)?.details;
  return typeof details?.reason === 'string' ? details.reason : null;
}

const WORKSPACE = '/api/v3/sales-workspace';

async function getData<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await axios.get<{ data: T }>(`${WORKSPACE}${path}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

export const readOpportunityPage = (id: string, signal?: AbortSignal): Promise<OpportunityPage> =>
  getData(`/opportunities/${encodeURIComponent(id)}`, signal);

export const readOpportunityEdit = (id: string, signal?: AbortSignal): Promise<OpportunityEditContext> =>
  getData(`/opportunities/${encodeURIComponent(id)}/edit`, signal);

export async function updateOpportunity(
  id: string,
  input: { readonly expectedLastModified: string; readonly fields: OpportunityFieldChanges },
): Promise<RepWriteApplied> {
  const response = await axios.patch<{ data: RepWriteApplied }>(`${WORKSPACE}/opportunities/${encodeURIComponent(id)}`, input, {
    headers: await authHeader(),
  });
  return response.data.data;
}

export async function logCall(id: string, input: { readonly subject: string; readonly notes?: string }): Promise<RepWriteApplied> {
  const response = await axios.post<{ data: RepWriteApplied }>(`${WORKSPACE}/opportunities/${encodeURIComponent(id)}/calls`, input, {
    headers: await authHeader(),
  });
  return response.data.data;
}

export async function completeFollowUp(
  taskId: string,
  input: {
    readonly expectedLastModified: string;
    readonly next: { readonly subject: string; readonly dueDate: string; readonly description?: string };
  },
): Promise<RepWriteApplied> {
  const response = await axios.post<{ data: RepWriteApplied }>(`${WORKSPACE}/follow-ups/${encodeURIComponent(taskId)}/complete`, input, {
    headers: await authHeader(),
  });
  return response.data.data;
}

/** The Connect Salesforce section's anchor on the rep's own profile. */
export const SALESFORCE_SECTION_ID = 'salesforce';
export const connectSalesforcePath = (userId: string): string => `/people/${encodeURIComponent(userId)}#${SALESFORCE_SECTION_ID}`;

const DAY_FORMAT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
const DAY_WITH_YEAR_FORMAT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

/** A `yyyy-mm-dd` day as "Fri, Oct 17" (it is a calendar day, so no time zone shift). */
export function formatDay(day: string | null, options: { readonly withYear?: boolean } = {}): string {
  if (!day) return '—';
  const date = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return '—';
  return (options.withYear ? DAY_WITH_YEAR_FORMAT : DAY_FORMAT).format(date);
}

/** `yyyy-mm-dd` a number of days after another. */
export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
